import { apiClient } from './client';
import type {
  LoginPhoto,
  LoginPhotoFilters,
  LoginPhotosResponse,
  LoginPhotoCreateData,
  LoginPhotoUpdateData,
  UploadResponse,
  ToggleActiveResponse,
} from '@/types/login-photo';

// Backend response format
interface BackendLoginPhotosResponse {
  items: LoginPhoto[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export async function getLoginPhotos(filters: LoginPhotoFilters = { page: 1, limit: 25, sortBy: 'order', sortOrder: 'DESC' }): Promise<LoginPhotosResponse> {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.theme !== undefined && filters.theme !== null && filters.theme !== '') {
    params.append('themeId', String(filters.theme));
  }
  if (filters.isActive !== undefined && filters.isActive !== null && filters.isActive !== '') {
    params.append('isActive', String(filters.isActive));
  }
  params.append('page', String(filters.page));
  params.append('limit', String(filters.limit));
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  const response = await apiClient.get<BackendLoginPhotosResponse>(`/api/admin/login-photos?${params.toString()}`);

  // Transform backend response to expected frontend format
  return {
    data: response.data.items,
    meta: {
      total: response.data.total,
      page: response.data.page,
      limit: response.data.limit,
      totalPages: response.data.totalPages,
    },
  };
}

export async function getLoginPhoto(id: number): Promise<LoginPhoto> {
  const response = await apiClient.get<LoginPhoto>(`/api/admin/login-photos/${id}`);
  return response.data;
}

export async function createLoginPhoto(data: LoginPhotoCreateData): Promise<{ id: number; message: string }> {
  const response = await apiClient.post<{ id: number; message: string }>('/api/admin/login-photos', data);
  return response.data;
}

export async function updateLoginPhoto(id: number, data: LoginPhotoUpdateData): Promise<{ id: number; message: string }> {
  const response = await apiClient.put<{ id: number; message: string }>(`/api/admin/login-photos/${id}`, data);
  return response.data;
}

export async function deleteLoginPhoto(id: number): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(`/api/admin/login-photos/${id}`);
  return response.data;
}

export async function uploadLoginPhotoImage(id: number, file: File): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<UploadResponse>(
    `/api/admin/login-photos/${id}/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

export async function reorderLoginPhotos(orderedIds: number[], swapDateIds?: [number, number]): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/api/admin/login-photos/reorder', {
    orderedIds,
    ...(swapDateIds ? { swapDateIds } : {}),
  });
  return response.data;
}

export async function toggleLoginPhotoActive(id: number): Promise<ToggleActiveResponse> {
  const response = await apiClient.put<ToggleActiveResponse>(`/api/admin/login-photos/${id}/toggle-active`);
  return response.data;
}

export interface CalendarDay {
  id: number | null;
  title: string;
  thumbnailUrl: string | null;
  isForced?: boolean;
  isRandom?: boolean;
  theme: string | null;
}

export interface CalendarResponse {
  year: number;
  month: number;
  calendar: Record<string, CalendarDay>;
}

export async function getLoginPhotosCalendar(year: number, month: number): Promise<CalendarResponse> {
  const response = await apiClient.get<CalendarResponse>(`/api/admin/login-photos/calendar?year=${year}&month=${month}`);
  return response.data;
}

export async function regenerateDates(): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/api/admin/login-photos/regenerate-dates');
  return response.data;
}

export async function setForcedDate(id: number, forcedDate: string | null): Promise<LoginPhoto> {
  const response = await apiClient.put<LoginPhoto>(`/api/admin/login-photos/${id}`, {
    forcedDate,
  });
  return response.data;
}
